/**
 * CouponInput Component
 * Input field for coupon code with real-time validation
 */
import { useState, useCallback } from 'react';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { useValidateCoupon } from '@/hooks/useBilling';
import type { CouponValidation } from '@/types/billing';

export interface CouponInputProps {
  onCouponValidated: (coupon: CouponValidation | null) => void;
  className?: string;
}

export default function CouponInput({
  onCouponValidated,
  className,
}: CouponInputProps) {
  const [code, setCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidation | null>(null);
  const validateCoupon = useValidateCoupon();

  const handleApply = useCallback(async () => {
    if (!code.trim()) return;

    try {
      const result = await validateCoupon.mutateAsync(code.trim().toUpperCase());
      if (result.valid) {
        setAppliedCoupon(result);
        onCouponValidated(result);
      } else {
        setAppliedCoupon(null);
        onCouponValidated(null);
      }
    } catch {
      setAppliedCoupon(null);
      onCouponValidated(null);
    }
  }, [code, validateCoupon, onCouponValidated]);

  const handleRemove = useCallback(() => {
    setCode('');
    setAppliedCoupon(null);
    onCouponValidated(null);
    validateCoupon.reset();
  }, [onCouponValidated, validateCoupon]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  // Determine validation state
  const hasError = validateCoupon.isError || (validateCoupon.data && !validateCoupon.data.valid);
  const errorMessage =
    validateCoupon.data?.errorMessage ||
    (validateCoupon.error?.message) ||
    'Invalid coupon code';

  // WP-BILLING-11: Check if this is a FREE_FOREVER coupon
  const isFreeForever = appliedCoupon?.discountType === 'free_forever';

  // WP-BILLING-13: Check if this is a FREE_MONTHS coupon
  const isFreeMonths = appliedCoupon?.discountType === 'free_months';

  return (
    <div className={cn('space-y-2', className)}>
      {/* Applied coupon display */}
      {appliedCoupon && appliedCoupon.valid && (
        <div
          className={cn(
            'flex items-center justify-between p-3 rounded-lg border',
            isFreeForever
              ? 'bg-purple-50 border-purple-300 ring-1 ring-purple-200'
              : isFreeMonths
              ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200'
              : 'bg-green-50 border-green-200'
          )}
        >
          <div className="flex items-center gap-2">
            {isFreeForever ? (
              // Special star icon for FREE_FOREVER
              <svg
                className="w-5 h-5 text-purple-500"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ) : isFreeMonths ? (
              // WP-BILLING-13: Clock/calendar icon for FREE_MONTHS (time-limited offer)
              <svg
                className="w-5 h-5 text-blue-500"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              // Standard checkmark icon
              <svg
                className="w-5 h-5 text-green-500"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            <div className="flex items-center gap-2">
              <span className={cn(
                'font-medium',
                isFreeForever
                  ? 'text-purple-800'
                  : isFreeMonths
                  ? 'text-blue-800'
                  : 'text-green-800'
              )}>
                {appliedCoupon.code}
              </span>
              {isFreeForever ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-purple-600 text-white">
                  FREE FOREVER
                </span>
              ) : isFreeMonths ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-blue-600 text-white">
                  {appliedCoupon.discountDisplay?.toUpperCase() || 'FREE TRIAL'}
                </span>
              ) : (
                <span className="text-green-700">{appliedCoupon.discountDisplay}</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className={cn(
              'text-sm font-medium',
              isFreeForever
                ? 'text-purple-600 hover:text-purple-800'
                : isFreeMonths
                ? 'text-blue-600 hover:text-blue-800'
                : 'text-green-600 hover:text-green-800'
            )}
            aria-label="Remove coupon"
          >
            Remove
          </button>
        </div>
      )}

      {/* Input field - hidden when coupon is applied */}
      {!appliedCoupon && (
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Enter coupon code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={handleKeyDown}
              error={hasError ? errorMessage : undefined}
              disabled={validateCoupon.isPending}
              aria-label="Coupon code"
            />
          </div>
          <Button
            variant="outline"
            onClick={handleApply}
            disabled={!code.trim() || validateCoupon.isPending}
            isLoading={validateCoupon.isPending}
            className="shrink-0"
          >
            Apply
          </Button>
        </div>
      )}

      {/* Coupon hint */}
      {!appliedCoupon && !hasError && (
        <p className="text-xs text-gray-500">
          Have a coupon code? Enter it above to get a discount.
        </p>
      )}
    </div>
  );
}
